import React, { ReactNode, useRef, useState } from "react";
import { getYear, startOfDay, subDays, endOfToday, subMonths, startOfYear, format, isSameMonth } from "date-fns";
import { DatePickerRangeInput } from "frontend/ui-components/date-picker-range/date-picker-range-input";
import commonSyles from "../search-common.module.css";
import { useSearchContext } from "frontend/boards-grid/search-section/context/search-context";
import { SearchFilterDate } from "frontend/boards-grid/search-section/types";
import styles from "./dates-filter.module.css";
import { Floater } from "frontend/ui-components/floaters/floater";
import { useOnClickOutside } from "usehooks-ts";

const CUSTOM_DATE = "Custom date";

const createOptions = (): { key: string; label: string; component?: ReactNode; startDate?: Date; endDate?: Date }[] => {
  return [
    { key: "today", label: "Today", startDate: startOfDay(new Date()), endDate: endOfToday() },
    {
      key: "last-7-days",
      label: "Last 7 days",
      startDate: subDays(new Date(), 7),
      endDate: endOfToday(),
    },
    {
      key: "last-30-days",
      label: "Last 30 days",
      startDate: subDays(new Date(), 30),
      endDate: endOfToday(),
    },
    {
      key: "last-6-months",
      label: "Last 6 months",
      startDate: subMonths(new Date(), 6),
      endDate: endOfToday(),
    },
    {
      key: "this-year",
      label: `This year (${getYear(new Date())})`,
      startDate: startOfYear(new Date()),
      endDate: endOfToday(),
    },
    {
      key: "custom",
      label: CUSTOM_DATE,
      component: <div className={styles.customDateOption}>{CUSTOM_DATE}</div>,
    },
  ];
};

const formatCustomDateLabel = (start: Date | undefined, end: Date | undefined) => {
  let label = `${start ? format(start, "MMM dd") : ""}`;
  if (start && end && isSameMonth(start, end)) {
    label += format(end, "-dd");
  } else {
    label += `${end ? format(end, "-MMM dd") : ""}`;
  }
  return label;
};

type DatesFilterProps = {
  onDateSelected: (onDateSelected: { startDate?: Date; endDate?: Date; label: ReactNode }) => void;
};

export const DatesFilter = ({ onDateSelected }: DatesFilterProps) => {
  const { filters } = useSearchContext();
  const [selected, setSelected] = useState("");
  const OPTIONS = useRef(createOptions());
  const dateFilter = filters.find((f) => f.type === "date") as SearchFilterDate;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const floaterRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(floaterRef, (event) => {
    if (selected === CUSTOM_DATE && event.target instanceof HTMLElement && !event.target.closest(`#custom`)) {
      setSelected("");
    }
  });

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      {OPTIONS.current.map((option) => {
        return (
          <div
            id={option.key}
            className={commonSyles.option}
            key={option.key}
            data-isselected={option.label === selected}
            onClick={() => {
              if (option.label === CUSTOM_DATE) {
                setSelected((selectedValue) => (selectedValue === option.label ? "" : option.label));
              } else {
                setSelected(option.label);
                onDateSelected(option);
              }
            }}
          >
            {option?.component ?? option.label}
          </div>
        );
      })}
      {selected === CUSTOM_DATE && (
        <Floater
          relativeTo={wrapperRef}
          ref={floaterRef}
          side="right"
          margin={-360}
          extraStyle={{
            zIndex: 1,
            backgroundColor: "white",
            boxShadow: "0px 0px 10px 0px #0000001a",
            borderRadius: "6px",
            padding: "10px",
          }}
        >
          <DatePickerRangeInput
            fromDate={dateFilter?.value?.startDate ? new Date(dateFilter?.value?.startDate) : new Date()}
            toDate={dateFilter?.value?.endDate ? new Date(dateFilter?.value?.endDate) : undefined}
            onSelected={(start, end) => {
              onDateSelected({
                label: formatCustomDateLabel(start, end),
                startDate: start,
                endDate: end ?? endOfToday(),
              });
              setSelected("");
            }}
            theme="light"
            showActions="both"
          />
        </Floater>
      )}
    </div>
  );
};
